import { Component, ElementRef, HostListener, Renderer2, SimpleChanges, ViewChild, ViewEncapsulation } from '@angular/core';
import { formatDate, getLocaleFirstDayOfWeek, Location } from '@angular/common';
import { faArrowLeft, faListRadio, faSliders, faSearch, faTimes, faRotateLeft } from '@fortawesome/pro-light-svg-icons';
import { faCircle } from '@fortawesome/pro-solid-svg-icons';
import { MatTableDataSource } from '@angular/material/table';
import { debounceTime, distinctUntilChanged, Observable, Subject } from 'rxjs';
import { RefundsService } from 'src/app/services/refunds.service';
import { NgbCalendar, NgbDate, NgbDateParserFormatter, NgbDatepickerNavigateEvent, NgbDateStruct, NgbInputDatepicker } from '@ng-bootstrap/ng-bootstrap';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { DialogRefundOrderComponent } from 'src/app/components/dialog-refund-order/dialog-refund-order.component';
import { AuthenticationService } from 'src/app/services/authentication.service';

@Component({
  selector: 'app-refunds',
  templateUrl: './refunds.component.html',
  styleUrls: ['./refunds.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class RefundsComponent {
  //icons
  faArrowLeft = faArrowLeft;
  faListRadio = faListRadio;
  faSliders = faSliders;
  faSearch = faSearch;
  faTimes = faTimes;
  faCircle = faCircle;
  faRotateLeft = faRotateLeft;

  //filters
  activeFilters: boolean = false;
  dateFilter!: any;
  selectedPriceRange!: any;
  priceFilter: any = { min: 0, max: 10000 };
  statusFilter!: any;
  methodFilter!: any;
  filterActive: boolean = false;
  openPriceRange: boolean = false;
  maxPriceEnabled: number = 10000;

  //date picker
  datesRange!: any;
  stringDate: string = '';
  openDatePicker: boolean = false;
  fromDate!: any;
  toDate!: any;
  hoveredDate!: any;
  currentMonth: number;
  minDate: NgbDateStruct;
  model: NgbDateStruct;
  @ViewChild('fechaDropdown') fechaDropdown!: ElementRef;
  @ViewChild('rangoDropdown') rangoDropdown!: ElementRef;

  //table
  dataSource = new MatTableDataSource<any>([]);
  displayedColumns: string[] = [
    'allow_refund',
    'id_order',
    'cash_register',
    'channel',
    'date',
    'price',
    'status'
  ];
  length: number = 0;
  currentPage: number = 1;
  lastPage: number = 1;
  order_by: string = '';
  order_direction: string = '';
  multiSelectMode: boolean = false;
  multiSelectArray: any = [];

  statusOptionsList: any = [];
  methodOptionsList: any = [];

  //search
  activeSearchItems: boolean = false;
  private searchTerms = new Subject<string>();
  searchText!: any;

  //loading
  loadingFilter: boolean = false;
  loadingMore: boolean = false;
  private scrollTimeout: any = null;

  constructor(private location: Location, private refundsService: RefundsService, private calendar: NgbCalendar, public formatter: NgbDateParserFormatter, private renderer: Renderer2, private router: Router, private dialog: MatDialog, private auth: AuthenticationService) {
    this.model = this.calendar.getToday();
    const today = new Date();
    this.minDate = {
      day: 1,
      month: 0,
      year: 2020
    };
    this.currentMonth = this.model.month;
  }

  ngOnInit() {
    this.refundsService.getStatusOptions().subscribe((res) => {
      this.statusOptionsList = res.data;
    });
    this.refundsService.getChannelOptions().subscribe((res) => {
      this.methodOptionsList = res.data;
    });
    this.refundsService.setLoadingTable(true);

    this.searchTerms.pipe(
      debounceTime(500),
      distinctUntilChanged(),
    ).subscribe(items => {
      this.activeSearchItems = false;
      this.refundsService.setSearchOrder(items);
    });

    this.refundsService.filtersOrderStorage.subscribe({
      next: (res) => {
        if (res) {
          this.searchText = res?.searchOrder;
          if (res?.filterOrders?.dateFilter) {
            this.dateFilter = res?.filterOrders.dateFilter;
            this.stringDate = `${this.formatStringDate(this.dateFilter.date_from)}`;
            if (this.dateFilter.date_to && this.dateFilter.date_to !== '') {
              this.stringDate += ' - ' + this.formatStringDate(this.dateFilter.date_to);
            }
          }
          if (res?.filterOrders?.priceFilter) {
            this.priceFilter = res?.filterOrders.priceFilter;
            this.selectedPriceRange = `${this.priceFilter.min}€ - ${this.priceFilter.max}€`;
          }
          if (res?.filterOrders?.statusFilter) {
            this.statusFilter = res?.filterOrders.statusFilter;
          }
          if (res?.filterOrders?.methodFilter) {
            this.methodFilter = res?.filterOrders.methodFilter;
          }
          this.updateTableData();
        }
      }
    });

    this.refundsService.loadingTableStorage.subscribe({
      next: (res) => {
        this.loadingFilter = res;
      }
    })
    this.updateTableData();
  }

  @HostListener('document:click', ['$event'])
  handleClickOutside(event: MouseEvent): void {
    const target = event.target as HTMLElement;

    if (
      this.openDatePicker &&
      !this.fechaDropdown.nativeElement.contains(target)
    ) {
      this.openDatePicker = false;
    }

    if (
      this.openPriceRange &&
      !this.rangoDropdown.nativeElement.contains(target)
    ) {
      this.openPriceRange = false;
    }
  }


  ngOnChanges(changes: SimpleChanges) {
    if (this.datesRange) {
      this.fromDate = this.formatter.parse(this.datesRange.min);
      this.toDate = this.formatter.parse(this.datesRange.max);
    } else {
      this.fromDate = null;
      this.toDate = null;
    }
  }

  searchOrder(event) {
    this.searchText = event;
    this.searchTerms.next(this.searchText);
  }

  goBack() {
    this.location.back();
  }

  updateFilter(active: boolean = true) {
    this.currentPage = 1; // Resetear página al filtrar
    this.filterActive = active;
    this.refundsService.setLoadingTable(true);
    this.refundsService.setFilters(this.dateFilter, this.priceFilter, this.statusFilter, this.methodFilter);
  }

  openFilters() {
    this.activeFilters = !this.activeFilters;
    if (!this.activeFilters && this.filterActive) {
      this.dateFilter = null;
      this.priceFilter = null;
      this.statusFilter = null;
      this.methodFilter = null;
      this.fromDate = null;
      this.toDate = null;
      this.clearFilters();
    }
  }

  clearFilters() {
    this.dateFilter = null;
    this.priceFilter = { min: 0, max: this.maxPriceEnabled };
    this.statusFilter = null;
    this.selectedPriceRange = null;
    this.stringDate = '';
    this.openPriceRange = false;
    this.filterActive = false;
    this.updateFilter(false);
  }


  //table

  updateTableData() {
    this.refundsService
      .getOrders(this.currentPage, this.order_by, this.order_direction, this.dateFilter?.date_from, this.dateFilter?.date_to, this.priceFilter.min, this.priceFilter.max, this.statusFilter, this.methodFilter, this.searchText)
      .subscribe((res) => {
        // Guardar información de paginación
        this.lastPage = res.data.pagination.last_page || 1;

        // Si es la primera página, reemplazar datos
        if (this.currentPage === 1) {
          this.dataSource = new MatTableDataSource(res.data.data);
        } else {
          // Si no es la primera página, añadir a los datos existentes
          const currentData = this.dataSource.data;
          this.dataSource.data = [...currentData, ...res.data.data];
        }

        this.refundsService.setLoadingTable(false);
        this.loadingMore = false;
      }, error => {
        this.refundsService.setLoadingTable(false);
        this.loadingMore = false;
      });
  }

  sortData(sort: any) {
    this.currentPage = 1; // Resetear página al ordenar
    this.order_by = sort.active;
    this.order_direction = sort.direction;
    this.updateTableData();
  }

  startRefund(id_transaction: any) {
    const dialogRef = this.dialog.open(DialogRefundOrderComponent, {
      width: '755px',
      height: 'auto',
      panelClass: 'rounded-dialog',
      data: { id_transaction: id_transaction }
    })
  }

  startMultiRefund() {
    this.auth.check().subscribe((data: any) => {
      let idWorkTime = localStorage.getItem('id_work_time');
      let payload = {
        work_time_cash_register_id: idWorkTime,
        admin_email: data.body.email,
        transaction_ids: this.multiSelectArray
      };
      // TODO: Masive refunds
    });
  }

  getStatusClass(status: any) {
    //return short_name of corresponding status in statusOptionsList
    return this.statusOptionsList.find((item: any) => item.name === status)?.short_name;
  }

  priceRangeChange() {
    this.selectedPriceRange = `${this.priceFilter.min}€ - ${this.priceFilter.max}€`;
    this.filterActive = true;
    this.updateFilter();
  };

  isHovered(date: NgbDate) {
    return this.fromDate && !this.toDate && this.hoveredDate && date.after(this.fromDate) &&
      date.before(this.hoveredDate);
  }
  isMonday = (date: NgbDate) => this.calendar.getWeekday(date) == this.getFirstDayWeek();
  isSunday = (date: NgbDate) => this.calendar.getWeekday(date) == this.getLastDayWeek();
  isWeekend = (date: NgbDate) => this.calendar.getWeekday(date) >= 6;
  isDisabled = (date: NgbDate) => !(date.equals(this.minDate) || (date.after(this.minDate) && (date.month == this.currentMonth)));

  isFromDate(date: NgbDate) {
    if (this.fromDate) {
      return date.equals(this.fromDate);
    }
    return false;
  }

  isToDate(date: NgbDate) {
    if (this.toDate) {
      return date.equals(this.toDate);
    }
    return false;
  }
  isInside(date: NgbDate) { return this.toDate && date.after(this.fromDate) && date.before(this.toDate); }

  isRange(date: NgbDate) {
    return (date.equals(this.fromDate) && this.toDate) || (this.toDate && date.equals(this.toDate)) || this.isInside(date);
  }
  getFirstDayWeek() {
    let first = getLocaleFirstDayOfWeek('es');
    return first;
  }

  getLastDayWeek() {
    let first = getLocaleFirstDayOfWeek('es');
    let lastCal = first + 6;
    if (lastCal > 7) {
      lastCal = first - (lastCal - 7)
    }
    return lastCal;
  }

  changeDate(date: NgbDate) {
    if (!this.fromDate && !this.toDate) {
      this.fromDate = date;
    } else if (this.fromDate && !this.toDate && date.after(this.fromDate)) {
      this.toDate = date;
      this.openDatePicker = false;
    } else {
      this.toDate = null;
      this.fromDate = date;
    }
    const dateString = { min: this.formatter.format(this.fromDate), max: this.formatter.format(this.toDate) };

    let stringDates = this.formatStringDate(dateString.min);
    if (dateString.max && dateString.max !== '') {
      stringDates += ' - ' + this.formatStringDate(dateString.max);
    }
    this.stringDate = stringDates;
    this.dateFilter = { date_from: this.formatStringDate(dateString.min, 'yyyy-MM-dd'), date_to: this.formatStringDate(dateString.max, 'yyyy-MM-dd') };
    this.filterActive = true;
    this.updateFilter();
  }
  dateNavigate($event: NgbDatepickerNavigateEvent) {
    this.currentMonth = $event.next.month;
  }

  formatStringDate(date: any, format: string = 'dd MMM yy') {
    let dateFormat = null;
    if (date) {
      dateFormat = formatDate(new Date(date), format, 'es');
    }
    return dateFormat;
  }

  check(event: any, row: any) {
    if (event) {
      this.multiSelectArray.push(row.id_transaction);
    } else {
      this.multiSelectArray = this.multiSelectArray.filter((item: any) => item !== row.id_transaction);
    }
  }

  goToDetail(id_transaction: any) {
    this.router.navigate(['/refunds/', id_transaction]);
  }

  @HostListener('window:scroll', ['$event'])
  onScroll(event: any) {
    // Limpiar el timeout anterior si existe
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }
    if (this.currentPage < this.lastPage) {
      this.loadingMore = true;
    }
    // Crear un nuevo timeout de 500ms
    this.scrollTimeout = setTimeout(() => {
      const tableWrapper = document.querySelector('.table-wrapper');
      if (tableWrapper) {
        const scrollPosition = window.pageYOffset + window.innerHeight;
        const tableBottom = tableWrapper.getBoundingClientRect().bottom + window.pageYOffset;

        // Si estamos cerca del final (100px antes del final), no estamos cargando y hay más páginas
        if (scrollPosition >= tableBottom - 100 && this.loadingMore && this.currentPage < this.lastPage) {
          this.currentPage++;
          this.updateTableData();
        }
      }
    }, 500);
  }
}
